import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import classNames from 'classnames';
import { BringgIcon, BringgFontIcons } from '@bringg/bringg-icons';
import { BringgInput } from 'components';
import { useInputWidth } from './helpers';

import './editable-title.scss';

const Text = Typography.Text;

export interface EditableTitleProps {
	value?: string;

	onValueChange?(newValue: string): void;

	onApplyChanges?(newValue: string): void;

	className?: string;
	placeholder?: string;
	maxLength?: number;
	maxEditLength?: number;
	loading?: boolean;
	tooltip?: boolean;
	forceShowEditIcon?: boolean;
}

const ESCAPE = 27;

const EditableTitle = ({
	value,
	onValueChange,
	onApplyChanges,
	className,
	placeholder = 'Untitled',
	maxLength = 32,
	maxEditLength = 60,
	loading,
	tooltip = true,
	forceShowEditIcon = false
}: EditableTitleProps) => {
	const [currentValue, setCurrentValue] = useState<string>(value || '');
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [beforeChangeValue, setBeforeChangeValue] = useState<string>(currentValue);
	const { inputWidth, fakeElRef } = useInputWidth(isEditMode, currentValue);

	useEffect(() => {
		setCurrentValue(value || '');
	}, [value]);

	const onKeyDown = e => {
		if (e.keyCode === ESCAPE) {
			setCurrentValue(beforeChangeValue);
			onValueChange?.(beforeChangeValue);
			setIsEditMode(false);
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentValue(e.target.value);
		onValueChange?.(e.target.value);
	};

	const onInputClick = e => {
		e.stopPropagation();
	};

	const applyChanges = e => {
		e.preventDefault();

		setBeforeChangeValue(currentValue);

		setIsEditMode(false);

		if (onApplyChanges) {
			onApplyChanges(currentValue);
		}
	};

	const toggleEdit = e => {
		e.stopPropagation();

		// Changing to be in edit mode now
		if (!isEditMode) {
			// save the value so can discard changes
			setBeforeChangeValue(currentValue);
		}
		setIsEditMode(prev => !prev);
	};

	return (
		<div className={classNames('editable-title', className, { loading })}>
			{isEditMode ? (
				<>
					<BringgInput
						type="text"
						className="editable-input"
						value={currentValue}
						onChange={onChange}
						onClick={onInputClick}
						onPressEnter={applyChanges}
						onKeyDown={onKeyDown}
						autoFocus
						placeholder={placeholder}
						maxLength={maxEditLength}
						onBlur={applyChanges}
						bordered={false}
						style={{ width: inputWidth }}
					/>
					<span ref={fakeElRef} className="input-sizer title" data-value={currentValue || placeholder}></span>
				</>
			) : (
				<div className={classNames('title', { 'no-value': !currentValue })} onClick={toggleEdit}>
					<Text
						className={classNames('title-inner-text', { 'no-value': !currentValue })}
						style={{ maxWidth: `${maxLength}ch` }}
						ellipsis={
							tooltip && currentValue.length > maxLength
								? { tooltip: { title: currentValue, overlayClassName: 'bringg-tooltip-overlay' } }
								: undefined
						}
					>
						{currentValue || placeholder}
					</Text>
					<BringgIcon
						className={classNames('edit-title-icon', { 'force-show-edit-icon': forceShowEditIcon })}
						iconName={BringgFontIcons.Pencil}
						data-test-id="edit-title-icon"
					/>
					{loading && (
						<BringgIcon
							iconName={BringgFontIcons.Loader}
							spin
							className="title-loader-icon"
							data-test-id="title-loader-icon"
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default EditableTitle;
